import { InvoiceFileNameEnum } from '@sr-sdks/api-sdk-axios';
import { Button, Icon, SR_ICONS } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { paidInvoicesParams } from '../api/payments/invoiceApi';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import EmptyValueErrorLayout from '../pages/Workspace/EmptyValueErrorLayout';
import { invoiceService } from '../reducers/payments/invoiceSlice';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import downloadApi from '../utils/api/downloadApi';
import {
  isLoadingState,
  isSuccessState,
  workspaceTypeIsAgent,
} from '../utils/helpers/common';
import { toDisbursementSavedFileName } from '../utils/helpers/statementFiles';
import './AppTable.css';
import AppTableV2, { AppTableV2HeaderCell as TH } from './AppTableV2';
import CurrencyFormatter from './CurrencyFormatter';
import DateFormatter from './DateFormatter';
import { ServiceBadge } from './Badges/ServiceBadge';

type LoadingType = { invoiceId: string; isLoading: boolean };

interface IPaymentsTableProps {
  isCollectionAccountMode?: boolean;
  isStaffView?: boolean;
  isWorkspaceSettingsPage?: boolean;
  titleId?: string;
  workspaceId?: string;
}

const PaymentsTable = ({
  isCollectionAccountMode = false,
  isStaffView = false,
  isWorkspaceSettingsPage,
  titleId,
  workspaceId,
}: IPaymentsTableProps) => {
  const dispatch = useAppDispatch();

  const [isLoadingDownloadDocument, setLoading] = useState<LoadingType>();

  const invoices = useTypedSelector(invoiceService.selectors.invoices);
  const { data: workspace } = useQuery(queryOptionsGetWorkspace(workspaceId));

  const isInvoicesLoading = useTypedSelector(
    invoiceService.selectors.isLoading,
  );
  const paginationMeta = useTypedSelector(
    invoiceService.selectors.paginationMeta,
  );

  const handleDownloadDocument = async (invoiceId: string) => {
    setLoading({ invoiceId, isLoading: true });

    const fileName = InvoiceFileNameEnum.DisbursementStatementPdf;

    try {
      const paymentInvoiceBlob = await downloadApi.paymentStatement(
        fileName,
        invoiceId,
      );

      downloadApi.downloadFile(
        paymentInvoiceBlob,
        toDisbursementSavedFileName(invoiceId, fileName),
      );
    } catch (error) {
      // Do nothing because ErrorModal is already handed globally
    } finally {
      setLoading({ invoiceId, isLoading: false });
    }
  };

  const handlePageRequest = useCallback(
    async (page: number, sortBy = 'invoiceDate:desc') => {
      if (!workspaceId) return;

      await dispatch(
        invoiceService.findAll(
          paidInvoicesParams({
            beneficiaryTitleId: titleId,
            beneficiaryWorkspaceId: workspaceId,
            page,
            pageSize: 10,
            sortBy,
          }),
        ),
      );
    },
    [workspaceId, dispatch, titleId],
  );

  useEffect(() => {
    if (!workspaceId) return;

    handlePageRequest(1);
  }, [dispatch, handlePageRequest, workspaceId]);

  if (
    isWorkspaceSettingsPage &&
    !isStaffView &&
    isSuccessState(isInvoicesLoading) &&
    invoices.length === 0
  ) {
    return <EmptyValueErrorLayout contentType="payment" />;
  }

  return (
    <>
      <AppTableV2
        id="payments-table"
        customEmptyTable={{
          description:
            'There are no payment records available for you to view at this time.',
          ...(!isStaffView &&
            isWorkspaceSettingsPage && { icon: SR_ICONS.folderOpen }),
          title: 'No payment records found',
        }}
        hasBottomNavigation={true}
        headerRowElement={
          <tr>
            <TH sortByProperty={'invoiceDate'}>Date</TH>
            {!isCollectionAccountMode && <TH>Title</TH>}
            {workspaceTypeIsAgent(workspace) && <TH>Client</TH>}
            {!isCollectionAccountMode && <TH>Service</TH>}
            <TH className="!text-right" sortByProperty={'amountNet'}>
              Amount
            </TH>
            <TH className="!text-center">Currency</TH>
            <TH hideLabel>Action</TH>
          </tr>
        }
        isLoading={isLoadingState(isInvoicesLoading)}
        paginationKey="paymentsTablePage"
        paginationMeta={paginationMeta}
        onPageRequest={handlePageRequest}
      >
        {invoices?.map((invoice) => (
          <tr key={invoice._id}>
            <td>
              <DateFormatter dateString={invoice.invoiceDate} />
            </td>
            {!isCollectionAccountMode && (
              <td>{invoice.beneficiaryTitleName || 'Unknown Title'}</td>
            )}
            {workspaceTypeIsAgent(workspace) && (
              <td>{invoice.beneficiaryClientName || 'Unknown Client'}</td>
            )}
            {!isCollectionAccountMode && (
              <td>
                <ServiceBadge serviceType={invoice.serviceType} />
              </td>
            )}
            <td className="!text-right">
              <CurrencyFormatter amount={invoice.amountNet} />
            </td>
            <td className="!text-center">{invoice.currency}</td>
            <td className="!text-center">
              <Button
                className="h-8 w-8"
                icon={<Icon icon="download" weight="fill" />}
                loading={
                  isLoadingDownloadDocument?.invoiceId === invoice._id &&
                  isLoadingDownloadDocument.isLoading
                }
                size="icon"
                onClick={() => handleDownloadDocument(invoice._id)}
              >
                <span className="sr-only">Download</span>
              </Button>
            </td>
          </tr>
        ))}
      </AppTableV2>
    </>
  );
};

export default PaymentsTable;
